import React, { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';
import { appReadySelector } from 'src/selector/app';
import { restauantProfileSelector } from 'src/selector/setting';
import { currentEmployeeSelector, permissionCodeSelector } from 'src/selector/user';
import { restaurantListSelector, isGroupUserSelector } from 'src/selector/app';
import { KEYS, USER_KEYS, getSessionItem } from 'src/store/storage';
import { loadPermissionCodes, logout } from 'src/actions/user';
import { shouldValidatePermission } from 'src/utils/utils';
import BasicLayout from 'src/layouts/basic';
import GroupLayout from 'src/layouts/groupLayout';
import UnactivatedResult from '../UnactivatedResult';
import UnauthorizedResult from '../UnauthorizedResult';
import UnauthorizedRg from 'src/components/RestGroupReports/components/UnauthorizedRg.jsx';
import PageNotFound from '../PageNotFound';
import { PageAccount } from 'src/consts/routes';
import PageLoading from '../PageLoading';
import { rgPermissionsSelector } from 'src/selector/rgUser';
import { rgPermissionCodes } from 'src/consts/rgMenu';

export const withContainer = (Component) => {
  return function WithContainer(props) {
    const ref = useRef();
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
      const container = ref.current;
      if (!container) return;

      function resizeObserverHandler(entries) {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect?.width || 0);
        }
      }

      const debouncedHandler = debounce(resizeObserverHandler, 150);
      const resizeObserver = new ResizeObserver(debouncedHandler);
      resizeObserver.observe(container);

      return () => {
        resizeObserver.unobserve(container);
      };
    }, []);

    return <div ref={ref}>{React.createElement(Component, { ...props, containerWidth })}</div>;
  };
};

/**
 * withPermissionCheck 权限检查高阶函数
 * @param {RC} React.FC
 * @param {Object} permissionCheckParams 权限检查参数
 * @param {string} permissionCheckParams.permissionCode 权限点
 * @param {string} permissionCheckParams.distributionMode 合约distributionMode (需要被合约控制的页面才需要传入这个参数)
 * @param {Function} permissionCheckParams.permissionChecker ({ restaurantInfo, userPermissionCodes, user })
 * @returns React.FC
 */
export const withPermissionCheck = (Component, { permissionCode, distributionMode, permissionChecker } = {}) => {
  return function WithPermissionCheck(props) {
    const router = useRouter();
    const dispatch = useDispatch();
    const appReady = useSelector(appReadySelector);
    const { isLoading: isLoadingProfile, restaurantInfo } = useSelector(restauantProfileSelector);
    const { permissionCodes, isLoadDone, isLoading } = useSelector(permissionCodeSelector);
    const currentEmployee = useSelector(currentEmployeeSelector);
    const isGroup = useSelector(isGroupUserSelector);

    const shouldValidateRoute = useMemo(() => shouldValidatePermission(restaurantInfo), [restaurantInfo]);

    useEffect(() => {
      if (!appReady) return;
      const token = getSessionItem(USER_KEYS.token);
      if (!token) {
        dispatch(logout());
        return;
      }
    }, [appReady]);

    const fetchPermissionCode = () => {
      dispatch(loadPermissionCodes());
    };

    // to prevent visiting other pages in cps
    if (getSessionItem(KEYS.isInCPS) === 'true') return null;
    if (!appReady) return null;

    if (isGroup) {
      return (
        <GroupLayout>
          <PageNotFound />
        </GroupLayout>
      );
    }

    if (isLoadingProfile) return null;

    if (isLoading && !isLoadDone) {
      return (
        <BasicLayout>
          <PageLoading absoluteCenter={true} background="transparent" />
        </BasicLayout>
      );
    }

    if (!isLoading && !isLoadDone) {
      return (
        <BasicLayout>
          <div style={{ paddingTop: 100, fontSize: 16, textAlign: 'center' }}>
            <a onClick={fetchPermissionCode}>
              <FormattedMessage id="reload-permissionCodes" />
            </a>
          </div>
        </BasicLayout>
      );
    }

    const hasValidator = !!permissionCode || !!permissionChecker;

    if (!shouldValidateRoute || !hasValidator) {
      return React.createElement(Component, props);
    }

    const restaurantActivated = getSessionItem(USER_KEYS.activated) === 'true';
    const { pathname } = router;
    let element;

    if (!restaurantActivated && (pathname === PageAccount || pathname.startsWith('/settings/'))) {
      element = <UnactivatedResult />;
    } else if (!permissionCodes.length) {
      element = <UnauthorizedResult router={router} />;
    } else {
      let noPermission = false;

      if (permissionCode) {
        noPermission = !permissionCodes.includes(permissionCode);
      } else if (typeof permissionChecker === 'function') {
        noPermission = !permissionChecker({
          restaurantInfo,
          userPermissionCodes: permissionCodes,
          user: currentEmployee,
        });
      }

      if (!noPermission && distributionMode) {
        const modes = (restaurantInfo.restaurant_contracts || []).map((_) => _.distribution_mode);
        noPermission = !modes.includes(distributionMode);
      }

      if (noPermission) {
        element = <UnauthorizedResult router={router} />;
      }
    }

    if (element) {
      return <BasicLayout>{element}</BasicLayout>;
    }

    return React.createElement(Component, props);
  };
};

export const withGroupPermissionCheck = (Component) => {
  return function WithGroupPermissionCheck(props) {
    const router = useRouter();
    const dispatch = useDispatch();
    const appReady = useSelector(appReadySelector);
    const isGroup = useSelector(isGroupUserSelector);
    const { curPermissions } = useSelector(rgPermissionsSelector);

    const { isLoading: isRestLoading } = useSelector(restaurantListSelector);

    useEffect(() => {
      if (!appReady) return;
      const token = getSessionItem(USER_KEYS.token);
      if (!token) {
        dispatch(logout());
      }
    }, [appReady]);

    if (!appReady) {
      return null;
    }

    // loading page
    if (isRestLoading || !curPermissions) {
      return (
        <GroupLayout>
          <PageLoading absoluteCenter={true} background="transparent" />
        </GroupLayout>
      );
    }

    if (!isGroup) {
      return (
        <BasicLayout>
          <PageNotFound />
        </BasicLayout>
      );
    }

    const curPermissionCode = rgPermissionCodes[router.pathname];
    if (curPermissionCode && !curPermissions.includes(curPermissionCode)) {
      const noPermissions = !curPermissions.length;
      return (
        <GroupLayout>
          <UnauthorizedRg
            router={router}
            isTitleEmpty={noPermissions}
            subTitleId={noPermissions ? 'rg.action.noMenu' : 'page403.subtitle'}
            showBackBtn={!noPermissions}
          />
        </GroupLayout>
      );
    }

    return React.createElement(Component, props);
  };
};

// use as React.createElement(withFitContent, { ...props }, children);
export const withFitContent = withContainer(({ containerWidth, children }) => {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !containerWidth) return;
    const clientWidth = element.clientWidth;
    if (clientWidth <= containerWidth) return;
    const scale = Number(containerWidth / clientWidth).toFixed(2);
    element.style.transform = `scale(${scale})`;
    element.style.transformOrigin = '0 50%';
    element.style.fontWeight = 'bold';
  }, [children, containerWidth]);

  return (
    <div ref={ref} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
      {children}
    </div>
  );
});
